import type { Dispatch, RefObject, SetStateAction } from 'react';
import type {
  CampaignsByCategory,
  PageType,
  TableColumn,
  TableData,
  VisibleCategories,
} from '@/types';
import campaignTypes from '@/data/campaignTypes.json';
import {
  CampaignCategory,
  formatCurrency,
  formatNumber,
  formatPercentage,
  replaceUnderscoreWithSpace,
  uppercaseFirstLetter,
} from '@/utils';
import { ImpressionsNotApplicableTooltip } from '@/components/tooltips/ImpressionsTooltips';
import Checkbox from '@/components/Checkbox';

/**
 * This is the table data for the campaign types table.
 * @param apiData - The API data.
 * @param pageType - The type of page. 'earnings' or 'payouts'
 * @param visibleCategories - The visible campaign categories.
 * @param setVisibleCategories - The function to set the visible categories.
 * @param popoverRefs - The popover refs.
 * @returns The table data.
 */
const TableContent = (
  apiData: CampaignsByCategory | undefined,
  pageType: PageType,
  visibleCategories: VisibleCategories,
  setVisibleCategories: Dispatch<SetStateAction<VisibleCategories>>,
  popoverRefs: {
    brandedContent: RefObject<HTMLDivElement>;
    social: RefObject<HTMLDivElement>;
    other: RefObject<HTMLDivElement>;
  }
): TableData[] => {
  if (!apiData) return [];
  const { ads, programmatic, sponsorships } = apiData;

  const handleCheckboxChange = (campaignCategory: CampaignCategory) => {
    setVisibleCategories({
      ...visibleCategories,
      [campaignCategory]: {
        ...visibleCategories[campaignCategory],
        visible: !visibleCategories[campaignCategory].visible,
      },
    });
  };

  const checkboxColumn = (campaignCategory: CampaignCategory) => {
    return (
      <Checkbox
        name={campaignCategory}
        checked={visibleCategories[campaignCategory].visible}
        onChange={() => handleCheckboxChange(campaignCategory)}
        color={visibleCategories[campaignCategory].color}
        className="pl-6 pr-3"
      />
    );
  };

  const campaignTypeColumn = (campaignType: CampaignCategory) => {
    return (
      <div className="flex flex-col gap-1">
        <span className="font-medium">
          {uppercaseFirstLetter(replaceUnderscoreWithSpace(campaignType))}
        </span>
        {campaignTypes[campaignType].length > 0 && (
          <span className="text-textMedGrey text-sm">{campaignTypes[campaignType].join(', ')}</span>
        )}
      </div>
    );
  };

  const impressionsNotApplicableColumn = (
    campaignCategory: 'brandedContent' | 'social' | 'other'
  ) => {
    return <ImpressionsNotApplicableTooltip popoverRef={popoverRefs[campaignCategory]} />;
  };

  const earningsColumn = (campaignCategory: CampaignCategory) => {
    return (
      <span className="font-medium">
        {formatCurrency(
          apiData[campaignCategory]?.total?.amount ?? 0,
          apiData[campaignCategory]?.total?.currency
        )}
      </span>
    );
  };

  const percentageColumn = (campaignCategory: CampaignCategory) => {
    return (
      <span className="text-textMedGrey pr-6" data-testid={`percentage-${campaignCategory}`}>
        {formatPercentage(apiData[campaignCategory].percentage, { round: false, decimals: 2 })}%
      </span>
    );
  };

  const campaignTypesCategoryConfig = [
    {
      id: 'ads',
      category: CampaignCategory.ADS,
      data: ads,
      hasImpressions: true,
    },
    {
      id: 'programmatic',
      category: CampaignCategory.PROGRAMMATIC,
      data: programmatic,
      hasImpressions: true,
    },
    {
      id: 'sponsorships',
      category: CampaignCategory.SPONSORSHIPS,
      data: sponsorships,
      hasImpressions: true,
    },
    {
      id: 'brandedContent',
      category: CampaignCategory.BRANDED_CONTENT,
      data: apiData.branded_content,
      hasImpressions: false,
      impressionsRef: 'brandedContent',
    },
    {
      id: 'social',
      category: CampaignCategory.SOCIAL,
      data: apiData.social,
      hasImpressions: false,
      impressionsRef: 'social',
    },
    {
      id: 'other',
      category: CampaignCategory.OTHER,
      data: apiData.other,
      hasImpressions: false,
      impressionsRef: 'other',
    },
  ];

  const generateRowColumns = (
    config: (typeof campaignTypesCategoryConfig)[number]
  ): TableColumn[] => {
    const columns: TableColumn[] = [];
    const { category, data, hasImpressions, impressionsRef } = config;

    if (pageType !== 'payouts') {
      columns.push({
        id: 'checkbox',
        value: checkboxColumn(category),
      });
    }

    columns.push({
      id: 'campaignType',
      value: campaignTypeColumn(category),
      className: pageType === 'payouts' ? 'h-[77px] pl-6' : 'h-[77px]',
    });

    if (hasImpressions) {
      columns.push({
        id: 'impressions',
        type: 'number',
        value: formatNumber('en', data.impressions),
        className: 'text-right',
      });
    } else {
      columns.push({
        id: 'impressions',
        type: 'number',
        value: impressionsNotApplicableColumn(
          impressionsRef as 'brandedContent' | 'social' | 'other'
        ),
        className: 'flex justify-end items-center h-[77px]',
      });
    }

    columns.push({
      id: 'earnings',
      type: 'number',
      value: earningsColumn(category),
      className: 'text-right',
    });

    columns.push({
      id: 'percentage',
      type: 'percentage',
      value: percentageColumn(category),
      className: 'text-right',
    });

    return columns;
  };

  return campaignTypesCategoryConfig.map((config) => ({
    id: config.id,
    columns: generateRowColumns(config),
  }));
};

export default TableContent;
